import { useState } from 'react';
import { useTenantNavigation } from '../navigation/useTenantNavigation';
import { authService } from '../../services/authentication/authService';

interface FormErrors {
  email?: string;
  password?: string;
  general?: string;
}

export const useAuth = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [errors, setErrors] = useState<FormErrors>({});
  const { navigateWithTenant } = useTenantNavigation();

  // CAPTCHA state
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState<string | null>(null);

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({}); // Clear previous errors
    setCaptchaError(null);

    try {
      const response = await authService.signIn(email, password, captchaToken || undefined);

      if (response.requiresCaptcha) {
        setShowCaptcha(true);
        setCaptchaError(response.error || 'Please complete the CAPTCHA to continue.');
        return;
      }

      if (response.status === "FIELD_ERROR") {
        const newErrors: FormErrors = {};
        response.formFields?.forEach(formField => {
          if (formField.id === "email") {
            newErrors.email = formField.error;
          }
        });
        setErrors(newErrors);
      } else if (response.status === "WRONG_CREDENTIALS_ERROR") {
        setErrors({ general: "Email password combination is incorrect." });
      } else if (response.status === "SIGN_IN_NOT_ALLOWED") {
        setErrors({ general: response.reason || "Sign in not allowed." });
      } else {
        setShowCaptcha(false);
        setCaptchaToken(null);
        navigateWithTenant('/dashboard');
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err instanceof Error && 'isSuperTokensGeneralError' in err && err.isSuperTokensGeneralError === true) {
        setErrors({ general: err.message });
      } else {
        setErrors({ general: "Oops! Something went wrong." });
      }
    }
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    errors,
    handleSignIn,
    showCaptcha,
    setShowCaptcha,
    captchaToken,
    setCaptchaToken,
    captchaError,
    setCaptchaError
  };
}; 